import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DocumentLineEditor } from "../components/documents/DocumentLineEditor";
import { DocumentTextField } from "../components/documents/DocumentTextField";
import { DocumentRichTextEditor } from "../components/documents/DocumentRichTextEditor";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "../components/ui/alert-dialog";
import { 
  getDocument, 
  createDocument, 
  updateDocument, 
  validateDocument,
  getDocumentPdf,
  getCustomers,
  getCatalogItems,
  createCustomer,
  createCatalogItem,
  duplicateDocument,
  getCompanySettings,
  createRecurringInvoice
} from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  Save, 
  CheckCircle2, 
  Download, 
  ArrowLeft,
  Plus,
  Trash2,
  Search,
  Eye,
  X,
  ChevronDown,
  UserPlus,
  Package,
  FileText,
  Building2,
  User,
  RefreshCw,
  GripVertical
} from "lucide-react";
import { toast } from "sonner";

const DOC_TYPE_LABELS = {
  quote: "Devis",
  invoice: "Facture",
  credit: "Avoir"
};

const UNIT_OPTIONS = [
  { value: "flat", label: "Forfait" },
  { value: "hour", label: "Heure" },
  { value: "day", label: "Jour" },
  { value: "piece", label: "Pièce" },
  { value: "month", label: "Mois" },
  { value: "project", label: "Projet" }
];

const VAT_OPTIONS = [
  { value: "20", label: "20%" },
  { value: "10", label: "10%" },
  { value: "5.5", label: "5.5%" },
  { value: "2.1", label: "2.1%" },
  { value: "0", label: "0%" }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR"
  }).format(amount || 0);
};

const DocumentEditorPage = () => {
  const { id, type } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new" || !id;
  const docType = isNew ? type : null;

  // States
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const actionInFlight = useRef(false);
  const [validateConfirmOpen, setValidateConfirmOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCatalogDropdown, setShowCatalogDropdown] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Quick create dialogs
  const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);
  const [quickCatalogOpen, setQuickCatalogOpen] = useState(false);
  const [quickCustomerForm, setQuickCustomerForm] = useState({
    customer_type: "professional",
    company_name: "",
    first_name: "",
    last_name: "",
    address_complement: "",
    email: "",
    address: "",
    postal_code: "",
    city: ""
  });
  const [quickCatalogForm, setQuickCatalogForm] = useState({
    item_type: "service",
    name: "",
    unit: "flat",
    unit_price_ht: 0,
    vat_rate: 20
  });

  // Recurring invoice options
  const [makeRecurring, setMakeRecurring] = useState(false);
  const [recurringOptions, setRecurringOptions] = useState({
    name: "",
    frequency: "monthly",
    frequency_value: 1,
    day_of_month: 1,
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
  });

  const customerDropdownRef = useRef(null);

  // Initialize document
  useEffect(() => {
    if (isNew) {
      const today = new Date().toISOString().split("T")[0];
      const due = new Date();
      due.setDate(due.getDate() + 30);
      
      setDocument({
        doc_type: docType,
        status: "draft",
        customer_id: "",
        customer_snapshot: null,
        subject: "",
        details: "",
        project_reference: "",
        issue_date: today,
        due_date: due.toISOString().split("T")[0],
        payment_delay_days: 30,
        lines: [],
        global_discount_percent: 0,
        global_discount_amount: 0,
        deposit_percent: 0,
        deposit_amount: 0,
        specific_note: "",
        // Payment & legal terms (will be defaulted from settings on save)
        payment_terms: "",
        late_penalty_rate: 10,
        late_penalty_text: "",
        recovery_indemnity: 40,
        legal_mentions: "",
        // Totals
        subtotal_ht: 0,
        total_discount: 0,
        total_ht: 0,
        total_vat: 0,
        total_ttc: 0,
        amount_due: 0
      });
      // Load settings for defaults
      loadSettings();
    } else {
      loadDocument();
    }
    loadCustomers();
    loadCatalogItems();
  }, [id, type]);

  // Load settings for default payment terms
  const loadSettings = async () => {
    try {
      const res = await getCompanySettings();
      const s = res.data;
      
      // Get the appropriate default mention based on document type
      let defaultMention = "";
      if (type === "quote") {
        defaultMention = s.default_quote_mention || "En cas de validation du devis, un acompte de 30 % est exigé à la signature du devis.\nLe solde est payable à la livraison de la prestation.";
      } else if (type === "invoice") {
        defaultMention = s.default_invoice_mention || "En votre aimable règlement.";
      }
      // Credit notes don't have a default mention
      
      setDocument(prev => ({
        ...prev,
        payment_terms: s.default_payment_terms || "30 jours fin de mois",
        late_penalty_rate: s.late_penalty_rate || 10,
        late_penalty_text: s.late_penalty_text || `En cas de retard de paiement, une pénalité de ${s.late_penalty_rate || 10}% sera appliquée.`,
        recovery_indemnity: s.recovery_indemnity || 40,
        legal_mentions: defaultMention,
        payment_delay_days: s.default_payment_delay_days || 30
      }));
    } catch (err) {
      // Use defaults
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target)) {
        setShowCustomerDropdown(false);
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadDocument = async () => {
    try {
      const res = await getDocument(id);
      // Ensure all lines have unique IDs for drag & drop
      const docWithLineIds = {
        ...res.data,
        lines: res.data.lines.map((line, idx) => ({
          ...line,
          id: line.id || `line_${Date.now()}_${idx}`
        }))
      };
      setDocument(docWithLineIds);

      // For drafts: refresh customer snapshot with latest data
      if (res.data.status === "draft" && res.data.customer_id) {
        try {
          const custRes = await getCustomers({});
          const freshCustomer = custRes.data.find(c => c.id === res.data.customer_id);
          if (freshCustomer) {
            setDocument(prev => ({
              ...prev,
              customer_snapshot: {
                id: freshCustomer.id,
                customer_type: freshCustomer.customer_type,
                company_name: freshCustomer.company_name,
                first_name: freshCustomer.first_name,
                last_name: freshCustomer.last_name,
                address_complement: freshCustomer.address_complement,
                address: freshCustomer.address,
                city: freshCustomer.city,
                postal_code: freshCustomer.postal_code,
                country: freshCustomer.country,
                email: freshCustomer.email,
                phone: freshCustomer.phone,
                siret: freshCustomer.siret,
                vat_number: freshCustomer.vat_number,
              }
            }));
          }
        } catch {}
      }
    } catch (err) {
      toast.error("Document non trouvé");
      navigate("/documents");
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await getCustomers({});
      setCustomers(res.data);
      const preselectId = new URLSearchParams(window.location.search).get("customer");
      if (isNew && preselectId) {
        const c = res.data.find(x => x.id === preselectId);
        if (c) selectCustomer(c);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadCatalogItems = async () => {
    try {
      const res = await getCatalogItems({});
      setCatalogItems(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Calculate totals
  const calculateTotals = useCallback((doc) => {
    if (!doc) return doc;
    
    let subtotal_ht = 0;
    const lines = doc.lines.map(line => {
      const gross = (line.quantity || 0) * (line.unit_price_ht || 0);
      const discount = (line.discount_amount || 0) + (gross * (line.discount_percent || 0) / 100);
      const total_ht = Math.round((gross - discount) * 100) / 100;
      const total_vat = Math.round(total_ht * (line.vat_rate || 20) / 100 * 100) / 100;
      const total_ttc = Math.round((total_ht + total_vat) * 100) / 100;
      subtotal_ht += total_ht;
      return { ...line, total_ht, total_vat, total_ttc };
    });

    const global_discount = (doc.global_discount_amount || 0) + 
      (subtotal_ht * (doc.global_discount_percent || 0) / 100);
    const total_ht = Math.round((subtotal_ht - global_discount) * 100) / 100;
    
    let total_vat = 0;
    lines.forEach(line => {
      const proportion = subtotal_ht > 0 ? line.total_ht / subtotal_ht : 0;
      const adjusted_ht = line.total_ht - (global_discount * proportion);
      total_vat += adjusted_ht * (line.vat_rate || 20) / 100;
    });
    total_vat = Math.round(total_vat * 100) / 100;
    
    const total_ttc = Math.round((total_ht + total_vat) * 100) / 100;
    const deposit_applied = (doc.deposit_amount || 0) + 
      (total_ttc * (doc.deposit_percent || 0) / 100);
    const amount_due = Math.round((total_ttc - deposit_applied - (doc.amount_paid || 0) - (doc.total_credited || 0)) * 100) / 100;

    return {
      ...doc,
      lines,
      subtotal_ht: Math.round(subtotal_ht * 100) / 100,
      total_discount: Math.round(global_discount * 100) / 100,
      total_ht,
      total_vat,
      total_ttc,
      deposit_applied: Math.round(deposit_applied * 100) / 100,
      amount_due
    };
  }, []);

  const updateDoc = (updates) => {
    setDocument(prev => calculateTotals({ ...prev, ...updates }));
  };

  // Select customer
  const selectCustomer = (customer) => {
    updateDoc({ 
      customer_id: customer.id,
      customer_snapshot: {
        id: customer.id,
        customer_type: customer.customer_type,
        company_name: customer.company_name,
        first_name: customer.first_name,
        last_name: customer.last_name,
        address_complement: customer.address_complement,
        address: customer.address,
        city: customer.city,
        postal_code: customer.postal_code,
        country: customer.country,
        email: customer.email,
        phone: customer.phone,
        siret: customer.siret,
        vat_number: customer.vat_number
      }
    });
    setShowCustomerDropdown(false);
    setCustomerSearch("");
  };

  // Add line
  const addLine = (catalogItem = null) => {
    const newLine = catalogItem ? {
      id: `line_${Date.now()}`,
      name: catalogItem.name,
      description: catalogItem.description || "",
      description_html: catalogItem.description_html || null,
      unit: catalogItem.unit,
      quantity: 1,
      unit_price_ht: catalogItem.unit_price_ht,
      discount_percent: 0,
      discount_amount: 0,
      vat_rate: catalogItem.vat_rate,
      catalog_item_id: catalogItem.id
    } : {
      id: `line_${Date.now()}`,
      name: "",
      description: "",
      unit: "flat",
      quantity: 1,
      unit_price_ht: 0,
      discount_percent: 0,
      discount_amount: 0,
      vat_rate: 20
    };
    updateDoc({ lines: [...document.lines, newLine] });
    setShowCatalogDropdown(false);
  };

  const updateLine = (index, updates) => {
    setDocument(previous => {
      const lines = [...previous.lines];
      lines[index] = { ...lines[index], ...updates };
      return calculateTotals({ ...previous, lines });
    });
  };

  const removeLine = (index) => {
    updateDoc({ lines: document.lines.filter((_, i) => i !== index) });
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for reordering lines
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = document.lines.findIndex(line => line.id === active.id);
      const newIndex = document.lines.findIndex(line => line.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newLines = arrayMove(document.lines, oldIndex, newIndex);
        updateDoc({ lines: newLines });
      }
    }
  };

  // Quick create handlers
  const handleQuickCreateCustomer = async () => {
    // Validation selon le type de client
    if (quickCustomerForm.customer_type === "professional") {
      if (!quickCustomerForm.company_name?.trim()) {
        toast.error("La raison sociale est requise pour un professionnel");
        return;
      }
    } else {
      // Particulier : au moins prénom ou nom requis
      if (!quickCustomerForm.first_name?.trim() && !quickCustomerForm.last_name?.trim()) {
        toast.error("Le nom ou prénom est requis pour un particulier");
        return;
      }
    }
    try {
      // Ne pas envoyer les champs vides pour éviter les erreurs de validation
      const payload = {
        customer_type: quickCustomerForm.customer_type,
        ...(quickCustomerForm.company_name?.trim() && { company_name: quickCustomerForm.company_name.trim() }),
        ...(quickCustomerForm.first_name?.trim() && { first_name: quickCustomerForm.first_name.trim() }),
        ...(quickCustomerForm.last_name?.trim() && { last_name: quickCustomerForm.last_name.trim() }),
        ...(quickCustomerForm.address_complement?.trim() && { address_complement: quickCustomerForm.address_complement.trim() }),
        ...(quickCustomerForm.email?.trim() && { email: quickCustomerForm.email.trim() }),
        ...(quickCustomerForm.address?.trim() && { address: quickCustomerForm.address.trim() }),
        ...(quickCustomerForm.postal_code?.trim() && { postal_code: quickCustomerForm.postal_code.trim() }),
        ...(quickCustomerForm.city?.trim() && { city: quickCustomerForm.city.trim() }),
      };
      
      const res = await createCustomer(payload);
      toast.success("Client créé");
      setQuickCustomerOpen(false);
      loadCustomers();
      selectCustomer(res.data);
      setQuickCustomerForm({ 
        customer_type: "professional", 
        company_name: "", 
        first_name: "", 
        last_name: "", 
        address_complement: "",
        email: "", 
        address: "", 
        postal_code: "", 
        city: "" 
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleQuickCreateCatalog = async () => {
    if (!quickCatalogForm.name) {
      toast.error("Nom requis");
      return;
    }
    try {
      const res = await createCatalogItem(quickCatalogForm);
      toast.success("Article créé");
      setQuickCatalogOpen(false);
      loadCatalogItems();
      addLine(res.data);
      setQuickCatalogForm({ item_type: "service", name: "", unit: "flat", unit_price_ht: 0, vat_rate: 20 });
    } catch (err) {
      toast.error("Erreur");
    }
  };

  // Persist once and return the server snapshot. Errors must reach validation/PDF callers.
  const persistDocument = async () => {
    if (!document.customer_id) {
      throw new Error("Sélectionnez un client");
    }
    if (document.lines.length === 0) {
      throw new Error("Ajoutez au moins une ligne");
    }

    // Validate recurring options if enabled
    if (makeRecurring) {
      if (!recurringOptions.name) {
        throw new Error("Donnez un nom à la facture récurrente");
      }
      if (!recurringOptions.start_date) {
        throw new Error("Définissez une date de début pour la récurrence");
      }
    }

      const payload = {
        doc_type: document.doc_type,
        customer_id: document.customer_id,
        subject: document.subject,
        details: document.details,
        details_html: document.details_html || null,
        project_reference: document.project_reference,
        issue_date: document.issue_date,
        due_date: document.due_date,
        payment_delay_days: document.payment_delay_days,
        validity_date: document.validity_date,
        lines: document.lines.map(l => ({
          name: l.name,
          description: l.description,
          name_html: l.name_html || null,
          description_html: l.description_html || null,
          unit: l.unit,
          quantity: l.quantity,
          unit_price_ht: l.unit_price_ht,
          discount_percent: l.discount_percent,
          discount_amount: l.discount_amount,
          vat_rate: l.vat_rate,
          catalog_item_id: l.catalog_item_id
        })),
        global_discount_percent: document.global_discount_percent,
        global_discount_amount: document.global_discount_amount,
        deposit_percent: document.deposit_percent,
        deposit_amount: document.deposit_amount,
        specific_note: document.specific_note,
        legal_mentions: document.legal_mentions,
        payment_terms: document.payment_terms,
        late_penalty_rate: document.late_penalty_rate,
        late_penalty_text: document.late_penalty_text,
        recovery_indemnity: document.recovery_indemnity,
      };

      let savedDocId = document.id;
      let savedDocument;

      if (!document.id) {
        const res = await createDocument(payload);
        savedDocument = res.data;
        savedDocId = res.data.id;
        
        // Create recurring invoice if option is enabled
        if (makeRecurring && document.doc_type === "invoice") {
          try {
            const recurringPayload = {
              name: recurringOptions.name,
              customer_id: document.customer_id,
              subject: document.subject,
              details: document.details,
              details_html: document.details_html || null,
              payment_delay_days: document.payment_delay_days ?? 30,
              frequency: recurringOptions.frequency,
              frequency_value: recurringOptions.frequency_value,
              day_of_month: recurringOptions.frequency === "monthly" ? recurringOptions.day_of_month : null,
              start_date: recurringOptions.start_date,
              end_date: recurringOptions.end_date || null,
              lines: document.lines.map(l => ({
                name: l.name,
                description: l.description,
                name_html: l.name_html || null,
                description_html: l.description_html || null,
                unit: l.unit,
                quantity: l.quantity,
                unit_price_ht: l.unit_price_ht,
                vat_rate: l.vat_rate,
              })),
              specific_note: document.specific_note,
            };
            await createRecurringInvoice(recurringPayload);
          } catch (recurringErr) {
            toast.error("Facture créée mais erreur lors de la création de la récurrence");
            console.error("Recurring error:", recurringErr);
          }
        }
        
        navigate(`/document/${savedDocId}`, { replace: true });
      } else {
        savedDocument = (await updateDocument(document.id, payload)).data;
      }
      setDocument(savedDocument);
      return savedDocument;
  };

  const runDocumentAction = async action => {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setSaving(true);
    setSaveError("");
    try {
      await action();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === "string" ? detail : err.response ? "Impossible d’enregistrer le document. Vérifiez les champs puis réessayez." : err.message || "Enregistrement impossible.";
      setSaveError(message);
      toast.error(message);
    } finally {
      actionInFlight.current = false;
      setSaving(false);
    }
  };

  const handleSave = () => runDocumentAction(async () => {
    await persistDocument();
  });

  // Validate
  const handleValidate = async () => {
    if (actionInFlight.current) return;
    setValidateConfirmOpen(false);
    await runDocumentAction(async () => {
      const savedDocument = document.status === "draft" || document.doc_type === "quote" ? await persistDocument() : document;
      const { data } = await validateDocument(savedDocument.id);
      setDocument(data);
    });
  };

  // Preview PDF
  const handlePreview = async () => {
    if (actionInFlight.current) return;
    if (!document.id) {
      toast.error("Enregistrez d'abord le document");
      return;
    }
    
    const previewWindow = window.open("", "_blank");
    if (previewWindow) previewWindow.opener = null;
    await runDocumentAction(async () => {
      try {
      setPreviewLoading(true);
      const savedDocument = document.status === "draft" || document.doc_type === "quote" ? await persistDocument() : document;
      const res = await getDocumentPdf(savedDocument.id, false);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      if (previewWindow) previewWindow.location.href = url;
      else {
        const link = window.document.createElement("a"); link.href = url; link.download = `${DOC_TYPE_LABELS[savedDocument.doc_type]}_${savedDocument.number || "Brouillon"}.pdf`;
        window.document.body.appendChild(link); link.click(); link.remove();
      }
      
      // Cleanup after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      
      toast.success(previewWindow ? "PDF ouvert dans un nouvel onglet" : "PDF téléchargé");
    } catch (err) {
      previewWindow?.close();
      throw err;
    } finally {
      setPreviewLoading(false);
    }
    });
  };

  // Download PDF
  const handleDownload = async () => {
    await runDocumentAction(async () => {
      const savedDocument = document.status === "draft" || document.doc_type === "quote" ? await persistDocument() : document;
      const res = await getDocumentPdf(savedDocument.id, true);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `${DOC_TYPE_LABELS[savedDocument.doc_type]}_${savedDocument.number || "Brouillon"}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-slate-500">Chargement...</div>
      </div>
    );
  }

  if (!document) return null;

  const isLocked = document.status !== "draft" && document.doc_type !== "quote";
  // Le client peut être modifié même sur un document non brouillon, sauf si des paiements existent
  const isClientLocked = (document.amount_paid || 0) > 0 || (document.total_credited || 0) > 0;
  const clientName = document.customer_snapshot?.company_name || 
    `${document.customer_snapshot?.first_name || ""} ${document.customer_snapshot?.last_name || ""}`.trim();

  const filteredCustomers = customers.filter(c => 
    !customerSearch || 
    c.company_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.first_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.last_name?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const filteredCatalog = catalogItems.filter(item => 
    !catalogSearch || 
    item.name.toLowerCase().includes(catalogSearch.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col bg-slate-50" data-testid="document-editor-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            disabled={saving}
            data-testid="document-back-btn"
            aria-label="Retour"
            className="text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="font-heading text-lg text-slate-900 break-words" data-testid="document-title">
              {isNew ? `Nouvelle ${DOC_TYPE_LABELS[document.doc_type].toLowerCase()}` : 
                `${DOC_TYPE_LABELS[document.doc_type]} ${document.number || "(Brouillon)"}`}
            </h1>
          </div>
          {!isNew && (
            <Badge data-testid="document-status" className={`
              ${document.status === "draft" ? "bg-slate-100 text-slate-600" : 
                document.status === "paid" ? "bg-green-50 text-green-700" :
                "bg-blue-50 text-blue-700"}
            `}>
              {document.status === "draft" ? "Brouillon" : 
               document.status === "paid" ? "Payée" : "Validée"}
            </Badge>
          )}
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {!isNew && (
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={saving || previewLoading}
              data-testid="document-preview-btn"
              className="btn-outline"
            >
              <Eye size={16} className="mr-1.5" />
              Aperçu
            </Button>
          )}
          {!isNew && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={saving}
              data-testid="document-download-btn"
              className="btn-outline"
            >
              <Download size={16} className="mr-1.5" />
              PDF
            </Button>
          )}
          {!isLocked && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                data-testid="document-save-btn"
                className="btn-outline"
              >
                <Save size={16} className="mr-1.5" />
                {saving ? "..." : "Enregistrer"}
              </Button>
              {!isNew && (
                <Button
                  size="sm"
                  onClick={() => setValidateConfirmOpen(true)}
                  disabled={saving}
                  data-testid="document-validate-btn"
                  className="btn-primary"
                >
                  <CheckCircle2 size={16} className="mr-1.5" />
                  Valider
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {saveError && <div role="alert" className="mx-4 mt-3 border border-red-200 bg-red-50 rounded-md p-3 text-sm text-red-700" data-testid="document-save-error">{saveError} Vos modifications restent dans le formulaire.</div>}
      {/* Content */}
      <fieldset disabled={saving} className="min-w-0 flex-1 flex flex-col xl:flex-row">
        {/* Main Form */}
        <div className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
          <div className="min-w-0 max-w-4xl mx-auto space-y-6">
            {/* Client & Dates Row */}
            <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 [&>div]:min-w-0">
                {/* Client */}
                <div className="md:col-span-2" ref={customerDropdownRef}>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Client <span className="text-red-500">*</span>
                  </Label>
                  {isClientLocked ? (
                    <div className="px-4 py-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-900 font-medium">
                      {clientName || "—"}
                      <p className="text-xs text-slate-500 mt-1">Client verrouillé (paiements existants)</p>
                    </div>
                  ) : (
                    <div className="relative">
                      <div 
                        className="flex items-center border border-slate-200 rounded-lg bg-white cursor-pointer hover:border-slate-300 hover:shadow-sm transition-all"
                        onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                      >
                        <Search size={18} className="ml-4 text-slate-400" />
                        <input
                          type="text"
                          data-testid="customer-search-input"
                          placeholder="Rechercher un client..."
                          value={document.customer_id ? clientName : customerSearch}
                          onChange={(e) => {
                            setCustomerSearch(e.target.value);
                            if (document.customer_id) {
                              updateDoc({ customer_id: "", customer_snapshot: null });
                            }
                            setShowCustomerDropdown(true);
                          }}
                          className="flex-1 min-w-0 px-3 py-3 bg-transparent outline-none text-slate-900 placeholder:text-slate-400"
                        />
                        <ChevronDown size={18} className="mr-4 text-slate-400" />
                      </div>
                      
                      {showCustomerDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-auto">
                          {/* Quick create */}
                          <div
                            className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer flex items-center gap-2 text-[#00a67c]"
                            onClick={() => {
                              setShowCustomerDropdown(false);
                              setQuickCustomerOpen(true);
                            }}
                          >
                            <UserPlus size={16} />
                            <span className="font-medium text-sm">Créer un nouveau client</span>
                          </div>
                          {filteredCustomers.map(customer => (
                            <div
                              key={customer.id}
                              className="px-4 py-3 hover:bg-slate-50 cursor-pointer"
                              onClick={() => selectCustomer(customer)}
                            >
                              <p className="text-sm font-medium text-slate-900">
                                {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                              </p>
                              <p className="text-xs text-slate-500">
                                {customer.city || "—"} · {customer.customer_type === "professional" ? "Professionnel" : "Particulier"}
                              </p>
                            </div>
                          ))}
                          {filteredCustomers.length === 0 && (
                            <div className="px-4 py-3 text-sm text-slate-400">
                              Aucun client trouvé
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Dates & Délai */}
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">
                      Date d'émission
                    </Label>
                    <Input
                      type="date"
                      value={document.issue_date || ""}
                      onChange={(e) => {
                        const newIssueDate = e.target.value;
                        updateDoc({ issue_date: newIssueDate });
                        // Recalculate due date based on delay
                        if (document.payment_delay_days && document.doc_type === "invoice") {
                          const issueDate = new Date(newIssueDate);
                          issueDate.setDate(issueDate.getDate() + document.payment_delay_days);
                          updateDoc({ due_date: issueDate.toISOString().split("T")[0] });
                        }
                      }}
                      disabled={isLocked}
                      className="input-field py-3"
                    />
                  </div>
                  {document.doc_type === "invoice" && (
                    <>
                      <div>
                        <Label className="text-sm font-medium text-slate-700 mb-2 block">
                          Délai de paiement
                        </Label>
                        <Select
                          value={String(document.payment_delay_days ?? 30)}
                          onValueChange={(v) => {
                            const days = parseInt(v);
                            updateDoc({ payment_delay_days: days });
                            // Recalculate due date
                            if (document.issue_date) {
                              const issueDate = new Date(document.issue_date);
                              issueDate.setDate(issueDate.getDate() + days);
                              updateDoc({ due_date: issueDate.toISOString().split("T")[0] });
                            }
                          }}
                          disabled={isLocked}
                        >
                          <SelectTrigger className="input-field py-3">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">À réception</SelectItem>
                            <SelectItem value="7">7 jours</SelectItem>
                            <SelectItem value="10">10 jours</SelectItem>
                            <SelectItem value="14">14 jours</SelectItem>
                            <SelectItem value="30">30 jours</SelectItem>
                            <SelectItem value="45">45 jours</SelectItem>
                            <SelectItem value="60">60 jours</SelectItem>
                            <SelectItem value="90">90 jours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700 mb-2 block">
                          Échéance
                        </Label>
                        <Input
                          type="date"
                          value={document.due_date || ""}
                          onChange={(e) => updateDoc({ due_date: e.target.value })}
                          disabled={isLocked}
                          className="input-field py-3"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Subject */}
              <div className="mt-6">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Objet / Référence
                </Label>
                <Input
                  value={document.subject || ""}
                  data-testid="document-subject-input"
                  onChange={(e) => updateDoc({ subject: e.target.value })}
                  disabled={isLocked}
                  placeholder="Ex: Prestation vidéo événementielle"
                  className="input-field py-3"
                />
              </div>

              {/* Details */}
              <div className="mt-4">
                <Label htmlFor="document-details-input" className="text-sm font-normal text-slate-600 mb-2 block">
                  Détails complémentaires (optionnel)
                </Label>
                <DocumentRichTextEditor
                  testId="document-details-input"
                  value={document.details || ""}
                  html={document.details_html}
                  onChange={({ text, html }) => updateDoc({ details: text, details_html: html })}
                  readOnly={isLocked}
                  disabled={saving}
                  label="Détails complémentaires de la facture"
                />
              </div>

              {/* Recurring Invoice Option - Only for invoices in draft mode */}
              {document.doc_type === "invoice" && !isLocked && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="make-recurring"
                      checked={makeRecurring}
                      onChange={(e) => setMakeRecurring(e.target.checked)}
                      className="w-4 h-4 text-[#26788A] border-slate-300 rounded focus:ring-[#26788A]"
                    />
                    <Label htmlFor="make-recurring" className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-2">
                      <RefreshCw size={16} className="text-[#26788A]" />
                      Créer aussi une facture récurrente
                    </Label>
                  </div>
                  
                  {makeRecurring && (
                    <div className="mt-4 pt-4 border-t border-slate-200 space-y-4">
                      <div>
                        <Label className="text-sm text-slate-600 mb-1.5 block">Nom de la récurrence *</Label>
                        <Input
                          value={recurringOptions.name}
                          onChange={(e) => setRecurringOptions({ ...recurringOptions, name: e.target.value })}
                          placeholder="Ex: Abonnement mensuel - Client X"
                          className="input-field"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm text-slate-600 mb-1.5 block">Fréquence</Label>
                          <Select 
                            value={recurringOptions.frequency} 
                            onValueChange={(v) => setRecurringOptions({ ...recurringOptions, frequency: v })}
                          >
                            <SelectTrigger className="input-field">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="daily">Quotidienne</SelectItem>
                              <SelectItem value="weekly">Hebdomadaire</SelectItem>
                              <SelectItem value="monthly">Mensuelle</SelectItem>
                              <SelectItem value="quarterly">Trimestrielle</SelectItem>
                              <SelectItem value="yearly">Annuelle</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600 mb-1.5 block">Répéter tous les</Label>
                          <Input
                            type="number"
                            min="1"
                            value={recurringOptions.frequency_value}
                            onChange={(e) => setRecurringOptions({ ...recurringOptions, frequency_value: parseInt(e.target.value) || 1 })}
                            className="input-field"
                          />
                        </div>
                      </div>

                      {recurringOptions.frequency === "monthly" && (
                        <div>
                          <Label className="text-sm text-slate-600 mb-1.5 block">Jour du mois (1-28)</Label>
                          <Input
                            type="number"
                            min="1"
                            max="28"
                            value={recurringOptions.day_of_month}
                            onChange={(e) => setRecurringOptions({ ...recurringOptions, day_of_month: parseInt(e.target.value) || 1 })}
                            className="input-field w-20"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm text-slate-600 mb-1.5 block">Début de la récurrence *</Label>
                          <Input
                            type="date"
                            value={recurringOptions.start_date}
                            onChange={(e) => setRecurringOptions({ ...recurringOptions, start_date: e.target.value })}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-slate-600 mb-1.5 block">Fin (optionnel)</Label>
                          <Input
                            type="date"
                            value={recurringOptions.end_date}
                            onChange={(e) => setRecurringOptions({ ...recurringOptions, end_date: e.target.value })}
                            className="input-field"
                          />
                        </div>
                      </div>
                      
                      <p className="text-xs text-slate-500 mt-2">
                        La récurrence utilisera les mêmes lignes, client et conditions que cette facture.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Lines Table */}
            <div className="bg-white rounded-lg border border-slate-200">
              {/* Table Header */}
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 text-sm font-semibold text-slate-700" data-testid="document-lines-heading">Prestations</div>

              {/* Lines with Drag & Drop */}
              <div className="divide-y divide-slate-100">
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={document.lines.map(l => l.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {document.lines.map((line, index) => (
                      <DocumentLineEditor
                        key={line.id}
                        line={line}
                        index={index}
                        updateLine={updateLine}
                        removeLine={removeLine}
                        isLocked={isLocked}
                        disabled={saving}
                        formatCurrency={formatCurrency}
                      />
                    ))}
                  </SortableContext>
                </DndContext>

                {document.lines.length === 0 && (
                  <div className="px-4 py-8 text-center text-slate-400 text-sm">
                    Aucune ligne. Cliquez sur "Ajouter" pour commencer.
                  </div>
                )}
              </div>

              {/* Add Line */}
              {!isLocked && (
                <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
                  <div className="relative inline-block">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowCatalogDropdown(!showCatalogDropdown)}
                      className="text-[#00a67c] hover:text-[#008f6b] hover:bg-[#e6f7f3]"
                      data-testid="add-line-btn"
                    >
                      <Plus size={16} className="mr-1.5" />
                      Ajouter une ligne
                    </Button>
                    
                    {showCatalogDropdown && (
                      <div className="absolute left-0 z-50 w-80 max-w-[calc(100vw-64px)] mt-1 bg-white border border-slate-200 rounded-lg shadow-lg">
                        <div className="p-2 border-b border-slate-100">
                          <Input
                            placeholder="Rechercher dans le catalogue..."
                            data-testid="document-catalog-search"
                            value={catalogSearch}
                            onChange={(e) => setCatalogSearch(e.target.value)}
                            className="input-field text-sm"
                          />
                        </div>
                        <div className="max-h-64 overflow-auto">
                          <div
                            className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer flex items-center gap-2 border-b border-slate-100"
                            onClick={() => addLine()}
                            data-testid="document-add-empty-line"
                          >
                            <Plus size={14} className="text-slate-400" />
                            <span className="text-sm text-slate-700">Ligne vide</span>
                          </div>
                          <div
                            className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer flex items-center gap-2 text-[#00a67c] border-b border-slate-100"
                            onClick={() => {
                              setShowCatalogDropdown(false);
                              setQuickCatalogOpen(true);
                            }}
                          >
                            <Package size={14} />
                            <span className="text-sm font-medium">Créer un nouvel article</span>
                          </div>
                          {filteredCatalog.map(item => (
                            <div
                              key={item.id}
                              className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer"
                              onClick={() => addLine(item)}
                              data-testid={`document-add-catalog-${item.id}`}
                            >
                              <p className="text-sm text-slate-900">{item.name}</p>
                              <p className="text-xs text-slate-500">
                                {formatCurrency(item.unit_price_ht)} HT · {UNIT_OPTIONS.find(u => u.value === item.unit)?.label}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
                <Label htmlFor="document-note-input" className="text-sm font-medium text-slate-700 mb-1.5 block">
                Note (visible sur le document)
              </Label>
              <DocumentTextField
                id="document-note-input"
                testId="document-note-input"
                value={document.specific_note || ""}
                onChange={(e) => updateDoc({ specific_note: e.target.value })}
                readOnly={isLocked}
                placeholder="Conditions particulières, remarques..."
                className="input-field min-h-20"
              />
            </div>

            {/* Mention de pied de document */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                {type === "quote" ? "Mention devis" : type === "invoice" ? "Mention facture" : "Mention"}
              </Label>
              <p className="text-xs text-slate-500 mb-2">
                {type === "quote" 
                  ? "Conditions d'acompte et de paiement affichées en pied de devis" 
                  : type === "invoice"
                  ? "Mention de règlement affichée en pied de facture"
                  : "Mention affichée en pied de document"}
              </p>
              <DocumentTextField
                testId="document-legal-mentions-input"
                value={document.legal_mentions || ""}
                onChange={(e) => updateDoc({ legal_mentions: e.target.value })}
                readOnly={isLocked}
                placeholder={type === "quote" 
                  ? "En cas de validation du devis, un acompte de 30 %..." 
                  : "En votre aimable règlement."}
                className="input-field min-h-20"
              />
            </div>
          </div>
        </div>

        {/* Right Sidebar - Totals & Options */}
        <div className="min-w-0 w-full xl:w-80 xl:shrink-0 bg-white border-t xl:border-t-0 xl:border-l border-slate-200 p-6">
          {/* Totals */}
          <div className="space-y-4 mb-8">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Récapitulatif</h3>
            
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Sous-total HT</span>
              <span className="font-mono text-right tabular-nums text-slate-800">{formatCurrency(document.subtotal_ht)}</span>
            </div>
            
            {document.total_discount > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Remise</span>
                <span className="font-mono text-right tabular-nums text-orange-600">-{formatCurrency(document.total_discount)}</span>
              </div>
            )}
            
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Total HT</span>
              <span className="font-mono text-right tabular-nums font-medium text-slate-800" data-testid="document-total-ht">{formatCurrency(document.total_ht)}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-slate-600">TVA</span>
              <span className="font-mono text-right tabular-nums text-slate-800">{formatCurrency(document.total_vat)}</span>
            </div>
            
            <div className="h-px bg-slate-200 my-2" />
            
            <div className="flex justify-between items-center py-2 px-3 bg-slate-50 rounded-lg -mx-3">
              <span className="font-semibold text-slate-900">Total TTC</span>
              <span className="font-mono text-right tabular-nums text-2xl font-bold text-slate-900" data-testid="document-total-ttc">{formatCurrency(document.total_ttc)}</span>
            </div>
            
            {document.deposit_applied > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-slate-600">Acompte</span>
                <span className="font-mono text-right tabular-nums text-slate-800">-{formatCurrency(document.deposit_applied)}</span>
              </div>
            )}
            
            {(document.deposit_applied > 0 || document.amount_paid > 0) && (
              <>
                <div className="h-px bg-slate-200 my-2" />
                <div className="flex justify-between items-center py-2 px-3 bg-[#e6f7f3] rounded-lg -mx-3">
                  <span className="font-semibold text-slate-900">Reste à payer</span>
                  <span className="font-mono text-right tabular-nums text-xl font-bold text-[#00a67c]">{formatCurrency(document.amount_due)}</span>
                </div>
              </>
            )}
          </div>

          {/* Options */}
          {!isLocked && (
            <div className="space-y-5 pt-5 border-t border-slate-200">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Options</h3>
              
              {/* Discount */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Remise globale</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={document.global_discount_amount || ""}
                      onChange={(e) => updateDoc({ 
                        global_discount_amount: parseFloat(e.target.value) || 0,
                        global_discount_percent: 0
                      })}
                      placeholder="€"
                      className="input-field text-sm font-mono"
                    />
                  </div>
                  <span className="text-slate-400 self-center">ou</span>
                  <div className="flex-1">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={document.global_discount_percent || ""}
                      onChange={(e) => updateDoc({ 
                        global_discount_percent: parseFloat(e.target.value) || 0,
                        global_discount_amount: 0
                      })}
                      placeholder="%"
                      className="input-field text-sm font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Deposit */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Acompte</Label>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={document.deposit_amount || ""}
                      onChange={(e) => updateDoc({ 
                        deposit_amount: parseFloat(e.target.value) || 0,
                        deposit_percent: 0
                      })}
                      placeholder="€"
                      className="input-field text-sm font-mono"
                    />
                  </div>
                  <span className="text-slate-400 self-center">ou</span>
                  <div className="flex-1">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={document.deposit_percent || ""}
                      onChange={(e) => updateDoc({ 
                        deposit_percent: parseFloat(e.target.value) || 0,
                        deposit_amount: 0
                      })}
                      placeholder="%"
                      className="input-field text-sm font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Terms Section */}
          {!isLocked && document.doc_type === "invoice" && (
            <div className="space-y-5 pt-5 border-t border-slate-200">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Conditions de paiement</h3>
              
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Conditions</Label>
                <Select
                  value={document.payment_terms || "30 jours fin de mois"}
                  onValueChange={(v) => updateDoc({ payment_terms: v })}
                >
                  <SelectTrigger className="input-field text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="À réception">À réception</SelectItem>
                    <SelectItem value="7 jours">7 jours</SelectItem>
                    <SelectItem value="10 jours">10 jours</SelectItem>
                    <SelectItem value="30 jours">30 jours</SelectItem>
                    <SelectItem value="30 jours fin de mois">30 jours fin de mois</SelectItem>
                    <SelectItem value="45 jours">45 jours</SelectItem>
                    <SelectItem value="45 jours fin de mois">45 jours fin de mois</SelectItem>
                    <SelectItem value="60 jours">60 jours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Pénalités de retard (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={document.late_penalty_rate || 10}
                  onChange={(e) => {
                    const rate = parseFloat(e.target.value) || 10;
                    updateDoc({ 
                      late_penalty_rate: rate,
                      late_penalty_text: `En cas de retard de paiement, une pénalité de ${rate}% sera appliquée.`
                    });
                  }}
                  className="input-field text-sm font-mono"
                />
              </div>

              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Indemnité de recouvrement (€)</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={document.recovery_indemnity || 40}
                  onChange={(e) => updateDoc({ recovery_indemnity: parseFloat(e.target.value) || 40 })}
                  className="input-field text-sm font-mono"
                />
                <p className="text-xs text-slate-400 mt-1">Obligatoire pour les clients professionnels</p>
              </div>
            </div>
          )}

          {/* Notes Section */}
          {!isLocked && (
            <div className="space-y-5 pt-5 border-t border-slate-200">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Notes</h3>
              
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Note spécifique à ce document</Label>
                <Textarea
                  value={document.specific_note || ""}
                  onChange={(e) => updateDoc({ specific_note: e.target.value })}
                  placeholder="Note visible sur le PDF..."
                  rows={3}
                  className="input-field text-sm"
                />
              </div>
            </div>
          )}

          {/* Credit note info */}
          {document.doc_type === "credit" && document.original_invoice_number && (
            <div className="mt-6 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Facture d'origine</p>
              <p className="font-mono text-sm font-medium text-slate-900 mt-1">{document.original_invoice_number}</p>
            </div>
          )}
        </div>
      </fieldset>

      <AlertDialog open={validateConfirmOpen} onOpenChange={setValidateConfirmOpen}>
        <AlertDialogContent className="responsive-dialog max-w-md" data-testid="document-validate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-left" data-testid="document-validate-dialog-title">Valider {document.doc_type === "invoice" ? "la facture" : document.doc_type === "quote" ? "le devis" : "l’avoir"} ?</AlertDialogTitle>
            <AlertDialogDescription className="text-left" data-testid="document-validate-dialog-description">Les modifications seront enregistrées avant la validation. Le numéro sera attribué définitivement.{document.doc_type !== "quote" && " Le document ne sera ensuite plus modifiable."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel disabled={saving} data-testid="document-validate-cancel-btn">Annuler</AlertDialogCancel>
            <Button onClick={handleValidate} disabled={saving} data-testid="document-validate-confirm-btn">Enregistrer et valider</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick Customer Dialog */}
      <Dialog open={quickCustomerOpen} onOpenChange={setQuickCustomerOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={18} className="text-[#26788A]" />
              Nouveau client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Type de client */}
            <div>
              <Label className="text-sm text-slate-700 mb-2 block">Type de client</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    quickCustomerForm.customer_type === "professional"
                      ? "bg-[#26788A] text-white border-[#26788A]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => setQuickCustomerForm({ ...quickCustomerForm, customer_type: "professional" })}
                >
                  <Building2 size={16} className="inline mr-2" />
                  Professionnel
                </button>
                <button
                  type="button"
                  className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    quickCustomerForm.customer_type === "individual"
                      ? "bg-[#26788A] text-white border-[#26788A]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => setQuickCustomerForm({ ...quickCustomerForm, customer_type: "individual" })}
                >
                  <User size={16} className="inline mr-2" />
                  Particulier
                </button>
              </div>
            </div>

            {/* Champs selon le type */}
            {quickCustomerForm.customer_type === "professional" ? (
              <div>
                <Label className="text-sm text-slate-700">Raison sociale *</Label>
                <Input
                  value={quickCustomerForm.company_name}
                  onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, company_name: e.target.value })}
                  className="mt-1.5 input-field"
                  placeholder="Nom de l'entreprise"
                  autoFocus
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-slate-700">Prénom *</Label>
                  <Input
                    value={quickCustomerForm.first_name}
                    onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, first_name: e.target.value })}
                    className="mt-1.5 input-field"
                    placeholder="Prénom"
                    autoFocus
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Nom *</Label>
                  <Input
                    value={quickCustomerForm.last_name}
                    onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, last_name: e.target.value })}
                    className="mt-1.5 input-field"
                    placeholder="Nom"
                  />
                </div>
              </div>
            )}

            {/* Complément d'adresse / À l'attention de */}
            <div>
              <Label className="text-sm text-slate-700">Complément <span className="text-slate-400 text-xs">(optionnel)</span></Label>
              <Input
                value={quickCustomerForm.address_complement}
                onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, address_complement: e.target.value })}
                className="mt-1.5 input-field"
                placeholder="À l'attention de..., Titre, Service..."
              />
            </div>

            <div>
              <Label className="text-sm text-slate-700">Email <span className="text-slate-400 text-xs">(optionnel)</span></Label>
              <Input
                type="email"
                value={quickCustomerForm.email}
                onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, email: e.target.value })}
                className="mt-1.5 input-field"
                placeholder="email@exemple.com"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-sm text-slate-700">Adresse <span className="text-slate-400 text-xs">(optionnel)</span></Label>
                <Input
                  value={quickCustomerForm.address}
                  onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, address: e.target.value })}
                  className="mt-1.5 input-field"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-700">CP</Label>
                <Input
                  value={quickCustomerForm.postal_code}
                  onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, postal_code: e.target.value })}
                  className="mt-1.5 input-field"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm text-slate-700">Ville <span className="text-slate-400 text-xs">(optionnel)</span></Label>
              <Input
                value={quickCustomerForm.city}
                onChange={(e) => setQuickCustomerForm({ ...quickCustomerForm, city: e.target.value })}
                className="mt-1.5 input-field"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickCustomerOpen(false)} className="btn-outline">
              Annuler
            </Button>
            <Button onClick={handleQuickCreateCustomer} className="btn-primary">
              Créer le client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Catalog Dialog */}
      <Dialog open={quickCatalogOpen} onOpenChange={setQuickCatalogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={18} className="text-[#00a67c]" />
              Nouvel article
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm text-slate-700">Nom *</Label>
              <Input
                value={quickCatalogForm.name}
                onChange={(e) => setQuickCatalogForm({ ...quickCatalogForm, name: e.target.value })}
                className="mt-1.5 input-field"
                placeholder="Nom de l'article"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-sm text-slate-700">Unité</Label>
                <Select 
                  value={quickCatalogForm.unit}
                  onValueChange={(v) => setQuickCatalogForm({ ...quickCatalogForm, unit: v })}
                >
                  <SelectTrigger className="mt-1.5 input-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Prix HT</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={quickCatalogForm.unit_price_ht}
                  onChange={(e) => setQuickCatalogForm({ ...quickCatalogForm, unit_price_ht: parseFloat(e.target.value) || 0 })}
                  className="mt-1.5 input-field font-mono"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-700">TVA</Label>
                <Select 
                  value={String(quickCatalogForm.vat_rate)}
                  onValueChange={(v) => setQuickCatalogForm({ ...quickCatalogForm, vat_rate: parseFloat(v) })}
                >
                  <SelectTrigger className="mt-1.5 input-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VAT_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickCatalogOpen(false)} className="btn-outline">
              Annuler
            </Button>
            <Button onClick={handleQuickCreateCatalog} className="btn-primary">
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentEditorPage;
